import React, { useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Message } from '../types';

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  isSending?: boolean;
}

export const MessageList: React.FC<MessageListProps> = ({ messages, isLoading, isSending }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    // TODO: Add toast notification for copy success
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      {messages.length === 0 ? (
        <div className="text-center text-gray-500 mt-10">
          <p>Start a conversation by typing a message below.</p>
        </div>
      ) : (
        messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-3xl rounded-lg px-4 py-3 ${
                message.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-900'
              }`}
            >
              {/* Message Header */}
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium">
                  {message.role === 'user' ? 'You' : 'Assistant'}
                </span>
                <span className={`text-xs ${
                  message.role === 'user' ? 'text-blue-200' : 'text-gray-500'
                }`}>
                  {formatTimestamp(message.timestamp)}
                </span>
              </div>

              {/* Message Content */}
              {message.role === 'user' ? (
                <div className="text-sm whitespace-pre-wrap">{message.content}</div>
              ) : (
                <div className="prose prose-sm max-w-none prose-invert">
                  <ReactMarkdown
                    components={{
                      code({ node, inline, className, children, ...props }: any) {
                        const match = /language-(\w+)/.exec(className || '');
                        return !inline && match ? (
                          <div className="relative">
                            <button
                              onClick={() => handleCopy(String(children).replace(/\n$/, ''))}
                              className="absolute top-2 right-2 px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
                            >
                              Copy
                            </button>
                            <SyntaxHighlighter
                              style={vscDarkPlus}
                              language={match[1]}
                              PreTag="div"
                              {...props}
                            >
                              {String(children).replace(/\n$/, '')}
                            </SyntaxHighlighter>
                          </div>
                        ) : (
                          <code className={`${className} bg-gray-200 px-1 py-0.5 rounded`} {...props}>
                            {children}
                          </code>
                        );
                      },
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                </div>
              )}
              
              {/* File Attachments */}
              {message.files && message.files.length > 0 && (
                <div className="mt-2 space-y-1">
                  {message.files.filter(file => file && file.filename).map((file) => (
                    <div
                      key={file.id}
                      className={`flex items-center gap-2 px-2 py-1 rounded text-xs ${
                        message.role === 'user' ? 'bg-blue-500' : 'bg-gray-50'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="flex-1 truncate">{file.filename}</span>
                      <span className={message.role === 'user' ? 'text-blue-200' : 'text-gray-500'}>
                        ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                      {message.role === 'assistant' && (
                        <button
                          onClick={() => window.open(`https://brain-sand-1.onrender.com/api/files/download/${file.id}`, '_blank')}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Download
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Metadata */}
              {message.metadata && (
                <details className="mt-3 pt-3 border-t border-gray-200">
                  <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
                    View Details
                  </summary>
                  <pre className="mt-2 p-2 bg-gray-50 rounded text-xs overflow-x-auto">
                    {JSON.stringify(message.metadata, null, 2)}
                  </pre>
                </details>
              )}

              {/* Copy button for assistant messages */}
              {message.role === 'assistant' && (
                <button
                  className="mt-2 text-xs text-gray-500 hover:text-gray-700"
                  onClick={() => handleCopy(message.content)}
                >
                  Copy response
                </button>
              )}
            </div>
          </div>
        ))
      )}

      {/* Loading indicator */}
      {(isLoading || isSending) && (
        <div className="flex justify-start">
          <div className="bg-gray-100 text-gray-900 px-4 py-3 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
              <span className="text-sm text-gray-600">Thinking...</span>
            </div>
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};
